import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import type { ProjectResponse } from '@timelog/pm-shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePlanEditor } from './editor';

import { QueryKeys, useApiClient } from '@/api';

/**
 * Explicit 409 resolution (BR-20): the project changed elsewhere. "Keep mine"
 * re-PUTs the local document with the fresh version (overwriting the other
 * edit); "Take theirs" discards local changes. No silent merge in v1.
 */
export const ConflictDialog = () => {
  const { t } = useTranslation('pmPlanning');
  const { state, dispatch } = usePlanEditor();
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  const open = state.saveStatus === 'conflict';

  const fetchFresh = async (): Promise<ProjectResponse> => {
    const fresh = await api.get<ProjectResponse>(`/projects/${state.projectId}`);
    queryClient.setQueryData(QueryKeys.Project(state.projectId), fresh);
    return fresh;
  };

  const keepMine = async () => {
    setBusy(true);
    try {
      const fresh = await fetchFresh();
      dispatch({ type: 'RESOLVE_CONFLICT_KEEP_MINE', version: fresh.version });
    } finally {
      setBusy(false);
    }
  };

  const takeTheirs = async () => {
    setBusy(true);
    try {
      const fresh = await fetchFresh();
      dispatch({ type: 'ADOPT_SERVER', document: fresh.document, version: fresh.version });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} data-automation-id="conflict-dialog" maxWidth="xs" fullWidth>
      <DialogTitle>{t('conflict.Title')}</DialogTitle>
      <DialogContent>
        <Typography variant="bodyRegular" color="text.secondary">
          {t('conflict.Body')}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => void takeTheirs()}
          disabled={busy}
          color="inherit"
          data-automation-id="conflict-take-theirs"
        >
          {t('conflict.Discard')}
        </Button>
        <Button
          onClick={() => void keepMine()}
          disabled={busy}
          variant="contained"
          data-automation-id="conflict-keep-mine"
        >
          {t('conflict.Keep')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
